import cloneDeep from 'lodash/cloneDeep';
import keyBy from 'lodash/keyBy';
import { createReducer } from 'typesafe-actions';
import namespacedReducer from '../common/utils/new-namespaced-reducer.js';
import { SET_CURRENT_PROJECT } from '../project/action-types.js';
import {
  type Actions,
  clearSearch,
  initContentSearch,
  paginateBack,
  paginateForward,
  type SearchMeta,
  search,
  suggestTaxonomy,
  updateFilter,
  updateQuery,
  updateShapeIds,
  updateSort
} from './actions';
import type { CONTENT_SEARCH_NAME } from './constants.js';
import type { ContentSearchState } from './types.js';

export type ContentSearchSlice = {
  [CONTENT_SEARCH_NAME]: Record<string, ContentSearchState>;
};

export const initialContentSearchState: ContentSearchState = {
  terms: '',
  fields: [],
  sort: { field: '_updatedAt', order: 'desc' },
  page: 1,
  pageSize: 20,
  total: 0,
  totalPages: 1,
  shapeIds: [],
  results: [],
  taxonomyItemsById: {},
  taxonomySuggestions: [],
  filters: [],
  errors: [],
  status: false,
  loading: false,
  loaded: false,
  requested: false
};

type DeleteContentAction = {
  type: 'content/DELETE_CONTENT_FULFILLED';
  payload: unknown;
  meta: { contentId: string };
};

type SetCurrentProjectAction = {
  type: typeof SET_CURRENT_PROJECT;
  payload: {
    projectId: string;
    branch: string;
  };
  meta: undefined;
};

type AllActions = Actions | DeleteContentAction | SetCurrentProjectAction;

const reducer = createReducer<ContentSearchState, AllActions>(initialContentSearchState)
  .handleType(search.REQUESTED, (state, { payload }) => {
    const update = { page: 1, ...payload };
    return { ...state, ...update };
  })
  .handleType(search.PENDING, (state) => {
    return {
      ...state,
      loading: true,
      requested: false
    };
  })
  .handleType(search.FULFILLED, (state, { payload }) => {
    return {
      ...state,
      errors: [],
      loading: false,
      loaded: true,
      total: payload.total,
      totalPages: Math.ceil(payload.total / state.pageSize),
      results: payload.results
    };
  })
  .handleType(search.REJECTED, (state, { payload }) => {
    return {
      ...state,
      errors: [payload],
      loading: false,
      loaded: false
    };
  })
  .handleType(suggestTaxonomy.FULFILLED, (state, { payload }) => {
    const suggestions = payload.items.map((item) => item._id);
    const taxonomyItems = keyBy(payload.items, '_id');
    return {
      ...state,
      taxonomyItemsById: { ...state.taxonomyItemsById, ...taxonomyItems },
      taxonomySuggestions: suggestions
    };
  })
  .handleType(clearSearch.toString(), () => {
    // Hack to clear the form
    return null as unknown as ContentSearchState;
  })
  .handleType(paginateForward.toString(), (state) => {
    const { page } = state;
    return page === state.totalPages ? state : { ...state, page: page + 1 };
  })
  .handleType(paginateBack.toString(), (state) => {
    const { page } = state;
    return page === 1 ? state : { ...state, page: page - 1 };
  })
  .handleType(updateFilter.toString(), (state, { payload }) => {
    const update: Partial<ContentSearchState> = {
      filters: payload.selection,
      requested: true
    };
    if (payload.clearQuery) {
      update.terms = '';
    }

    return { ...state, ...update };
  })
  .handleType(updateQuery.toString(), (state, { payload }) => {
    return {
      ...state,
      terms: payload,
      page: 1,
      requested: true
    };
  })
  .handleType(updateSort.toString(), (state, { payload }) => {
    return {
      ...state,
      sort: { ...state.sort, ...payload },
      page: 1
    };
  })
  .handleType(updateShapeIds.toString(), (state, { payload }) => {
    return {
      ...state,
      shapeIds: payload,
      page: 1
    };
  })
  .handleType(initContentSearch.toString(), () => {
    return cloneDeep(initialContentSearchState);
  });

const globalReducer = createReducer<ContentSearchState, AllActions>(initialContentSearchState)
  .handleType('content/DELETE_CONTENT_FULFILLED', (state, { meta }) => {
    const { results } = state;
    const newResults = results.filter((result) => result._id !== meta.contentId);
    return {
      ...state,
      results: newResults,
      total: results.length === newResults.length ? state.total : state.total - 1
    };
  })
  .handleType(SET_CURRENT_PROJECT, () => {
    return cloneDeep(initialContentSearchState);
  });

export const contentSearchReducer = namespacedReducer(
  (action: AllActions): string | undefined =>
    action.meta && (action.meta as SearchMeta).searchName && 'searchName' in action.meta
      ? action.meta.searchName
      : undefined,
  initialContentSearchState,
  reducer,
  globalReducer
);
